/**
 * MappingCanvasContent — Embeds the mapping editor canvas inside the agent workspace.
 *
 * Wraps CanvasMappingEditor in "embedded" mode:
 * - No PreviewPanel (right panel is handled by MappingContextPanel)
 * - No DataPreviewModal (replaced by DataExplorerTab in bottom inspector)
 * - No "Back to Agent" breadcrumb (handled by MappingContextPanel's back button)
 * - Emits selection changes + mapping data to parent via callbacks
 *
 * Positioned in Cell 1 of the UnifiedCanvasLayout grid when workspaceMode === 'mapping'.
 */
import React from 'react';
import CanvasMappingEditor from '../CanvasMappingEditor';
import type { EmbeddedMutationHandlers } from '../CanvasMappingEditor';
import type { CanvasSelection, LineageChain } from '../../canvas/types';
import type { MappingResponse } from '../../services/types';
import type { CanvasManualAdjustmentModel, CanvasPromptRunRecordModel } from '../../services/api';

export interface MappingCanvasDataPayload {
  mappingResponse: MappingResponse | undefined;
  healthData: any;
  healthLoading: boolean;
  lineageChains: LineageChain[];
  manualAdjustments: CanvasManualAdjustmentModel[];
  promptRuns: CanvasPromptRunRecordModel[];
  edgeFieldMap: Record<string, string>;
}

export type { EmbeddedMutationHandlers };

export interface MappingCanvasContentProps {
  batchId: string;
  sessionId: string;
  onBack: () => void;
  onSelectionChange?: (selection: CanvasSelection | null) => void;
  onDataReady?: (data: MappingCanvasDataPayload) => void;
  /** Exposes mutation handlers to parent for LogicEditorTab. */
  onMutationHandlers?: (handlers: EmbeddedMutationHandlers) => void;
  /** Agent workspace destination export phase — drives mapping toolbar status chip. */
  destinationExportStatus?: string | null;
  /** Target schema ID from session meta — used when project has no target_schema_id. */
  targetSchemaIdOverride?: string | null;
}

const MappingCanvasContent: React.FC<MappingCanvasContentProps> = ({
  batchId,
  sessionId,
  onBack,
  onSelectionChange,
  onDataReady,
  onMutationHandlers,
  destinationExportStatus,
  targetSchemaIdOverride,
}) => {
  return (
    <CanvasMappingEditor
      batchIdProp={batchId}
      embedded
      onBack={onBack}
      onSelectionChange={onSelectionChange}
      onDataReady={onDataReady}
      onMutationHandlers={onMutationHandlers}
      sessionId={sessionId}
      destinationExportStatus={destinationExportStatus}
      targetSchemaIdOverride={targetSchemaIdOverride}
    />
  );
};

export default MappingCanvasContent;
